
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import {
  CodeBracketIcon,
  MagnifyingGlassIcon,
  ArrowPathRoundedSquareIcon,
  PauseIcon,
  ArrowUturnLeftIcon,
  DocumentDuplicateIcon,
  XCircleIcon,
  ClockIcon,
  InformationCircleIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  LightBulbIcon,
  BugAntIcon,
  RectangleStackIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const ManagerDashboard = () => {
  const [apps, setApps] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedStatusGroup] = useState('All');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Bug');
  const navigate = useNavigate();

  // Integrated professional color palette
  const COLORS = {
    Open: '#3B82F6',
    Fixed: '#10B981',
    InProgress: '#F59E0B',
    Critical: '#EF4444',
    Reopened: '#F97316',
    Deferred: '#CA8A04',
    Deprecated: '#6B7280',
    Grid: '#E5E7EB',
    Text: '#6B7280'
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    if (selectedAppId) {
      // Manual filter changes now use silent refresh to keep UI stable
      fetchDashboardData(selectedAppId, selectedStatusGroup, selectedUserId, true);
    }
  }, [selectedAppId, selectedStatusGroup, selectedUserId]);

  useEffect(() => {
    const handleBugUpdated = () => {
      fetchDashboardData(selectedAppId, selectedStatusGroup || 'All', selectedUserId);
    };
    const handleStorage = (e) => {
      if (e.key === 'bugtracker:bug-updated') {
        handleBugUpdated();
      }
    };
    window.addEventListener('bugtracker:bug-updated', handleBugUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('bugtracker:bug-updated', handleBugUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [selectedAppId, selectedStatusGroup, selectedUserId]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(selectedAppId, selectedStatusGroup || 'All', selectedUserId, true);
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedAppId, selectedStatusGroup, selectedUserId]);

  const fetchApplications = async () => {
    try {
      const res = await axios.get('/api/applications');
      const appList = res.data.data || res.data;
      setApps(appList);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setLoading(false);
    }
  };

  const fetchDashboardData = async (appId, statusGroup = 'All', userId = 'all', silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`/api/manager/dashboard/${appId}`, {
        params: { statusGroup, userId }
      });
      if (res.data.success) {
        setData(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navToBugs = (params = {}) => {
    const p = new URLSearchParams();
    if (selectedAppId && selectedAppId !== 'all') p.set('application', selectedAppId);
    Object.entries(params).forEach(([k, v]) => { if (v) p.set(k, v); });
    p.set('page', '1');
    navigate(`/manager/bugs?${p.toString()}`);
  };

  if (!loading && apps.length === 0 && selectedAppId !== 'all') return <div className="p-6 text-gray-500">No applications found.</div>;

  const currentAppName = selectedAppId === 'all' ? 'All Applications' : apps.find(a => a._id === selectedAppId)?.name || 'Unknown Application';

  const getActionText = (activity) => {
    switch (activity.action) {
      case 'created': return 'created';
      case 'status': return 'moved';
      case 'assignment': return 'assigned';
      case 'comment': return 'commented';
      default: return 'updated';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-auto min-h-[64px] py-2 flex flex-col justify-center">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-xl font-semibold text-gray-800 tracking-tight">Welcome back!</h1>
                  <p className="mt-0.5 text-xs text-gray-500">Track progress and manage team activity.</p>
                </div>
                <span className="h-6 w-px bg-gray-200 mx-2"></span>
                <select
                  value={selectedAppId}
                  onChange={(e) => setSelectedAppId(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 rounded-md py-1.5 pl-3 pr-8 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <option value="all">All Applications</option>
                  {apps.map(app => (
                    <option key={app._id} value={app._id}>{app.name}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-500 font-mono">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
            <div className="mt-1">
              <p className="text-xs text-gray-500 italic">
                Showing data for: <span className="font-medium text-gray-700">{currentAppName}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {loading || !data ? (
            <div className="flex justify-center items-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
            </div>
          ) : (
            <>
              {/* ── Top Row KPIs (5-up exact design) ─────────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div
                  onClick={() => navToBugs()}
                  className="cursor-pointer bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl p-3 flex items-center gap-3 shadow-md hover:shadow-lg transition-all group"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <RectangleStackIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-indigo-200 text-[9px] font-bold uppercase tracking-widest">Total Work Items</p>
                    <p className="text-2xl font-black text-white leading-none">{data.totalWorkItems || 0}</p>
                    <p className="text-indigo-200 text-[9px] mt-0.5">All ticket types</p>
                  </div>
                </div>

                {[
                  { label: 'Bugs',         icon: BugAntIcon,          iconColor: 'text-red-600',   accentBg: 'bg-red-50',   border: 'border-l-red-500',   textColor: 'text-red-700',   value: data.bugTypeCount || 0, filter: { ticketType: 'Bug' } },
                  { label: 'Observations', icon: LightBulbIcon,       iconColor: 'text-amber-500', accentBg: 'bg-amber-50', border: 'border-l-amber-500', textColor: 'text-amber-700', value: data.observationTypeCount || 0, filter: { ticketType: 'Observation' } },
                  { label: 'Improvements', icon: ArrowTrendingUpIcon, iconColor: 'text-green-600', accentBg: 'bg-green-50', border: 'border-l-green-500', textColor: 'text-green-700', value: data.improvementTypeCount || 0, filter: { ticketType: 'Improvement' } },
                ].map((t, i) => (
                  <div key={i} onClick={() => navToBugs(t.filter)}
                    className={`cursor-pointer bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${t.border} p-3 flex items-center gap-3 hover:shadow-md transition-all group`}>
                    <div className={`w-9 h-9 rounded-lg ${t.accentBg} flex items-center justify-center shrink-0`}>
                      <t.icon className={`h-5 w-5 ${t.iconColor}`} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{t.label}</p>
                      <p className={`text-2xl font-black ${t.textColor} leading-none`}>{t.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Detailed KPI Section (Tabbed) ─────────────────────────── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-2 p-3 border-b border-gray-50 bg-gray-50/30">
                  {[
                    { id: 'Bug', label: 'Bugs', icon: BugAntIcon, text: 'text-red-600', bgActive: 'bg-red-50', border: 'border-red-200', iconActive: 'text-red-500' },
                    { id: 'Observation', label: 'Observations', icon: LightBulbIcon, text: 'text-amber-600', bgActive: 'bg-amber-50', border: 'border-amber-200', iconActive: 'text-amber-500' },
                    { id: 'Improvement', label: 'Improvements', icon: ArrowTrendingUpIcon, text: 'text-emerald-600', bgActive: 'bg-emerald-50', border: 'border-emerald-200', iconActive: 'text-emerald-500' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                        activeTab === tab.id
                          ? `${tab.bgActive} ${tab.text} ${tab.border} shadow-sm`
                          : 'bg-transparent text-gray-500 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <tab.icon className={`h-3.5 w-3.5 ${activeTab === tab.id ? tab.iconActive : 'text-gray-400'}`} />
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="p-4 space-y-5">
                  {(() => {
                    const sec = {
                      'Bug': { statsKey: 'bugStats', ticketType: 'Bug', accent: 'text-red-700', borderColor: 'border-red-400', deferred: { a: 'text-amber-700', b: 'border-l-amber-500' } },
                      'Observation': { statsKey: 'observationStats', ticketType: 'Observation', accent: 'text-amber-700', borderColor: 'border-amber-400', deferred: { a: 'text-amber-700', b: 'border-l-amber-500' } },
                      'Improvement': { statsKey: 'improvementStats', ticketType: 'Improvement', accent: 'text-green-700', borderColor: 'border-green-400', deferred: { a: 'text-green-700', b: 'border-l-green-400' } },
                    }[activeTab];
                    
                    const src = data[sec.statsKey] || {};
                    const statusMapData = {
                      'Open': 'Open',
                      'Dev In Progress': 'In Progress',
                      'QA In Progress': 'Retesting In Progress',
                      'Recurring Issue': 'Recurring Issue',
                      'On Hold': 'On Hold',
                      'Reopened': 'Reopen',
                      'Fixed': 'Fixed',
                      'Closed': 'Closed',
                      'Duplicate': 'Duplicate',
                      'Rejected': 'Rejected',
                      'Deferred': 'Deferred',
                      'Not a Bug': 'Not a Bug',
                      'Deprecated': 'Deprecated',
                    };

                    const nav = (label, isArchived = false, isTotal = false) => {
                      const p = new URLSearchParams();
                      if (isArchived) { sessionStorage.setItem('bugListViewMode', 'archived'); p.set('view', 'archived'); }
                      else if (!isTotal) {
                        const status = statusMapData[label];
                        if (status) p.set('status', status);
                      }
                      p.set('ticketType', sec.ticketType);
                      if (selectedAppId && selectedAppId !== 'all') p.set('application', selectedAppId);
                      p.set('page', '1');
                      navigate(`/manager/bugs?${p.toString()}`);
                    };

                    return (
                      <div className="space-y-5">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div onClick={() => nav(null, false, true)} className="cursor-pointer bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                            <span className={`inline-block px-2 py-0.5 rounded-full ${sec.accent.replace('text-', 'bg-').replace('700', '50')} ${sec.accent} text-[9px] font-black uppercase tracking-widest mb-2`}>
                              TOTAL {sec.ticketType.toUpperCase()}S
                            </span>
                            <p className={`text-3xl font-black ${sec.accent} leading-none mb-1`}>{src.active || 0}</p>
                            <p className="text-[10px] text-gray-500 font-medium">Active workload</p>
                          </div>
                          <div onClick={() => nav('Open')} className="cursor-pointer bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[9px] font-black uppercase tracking-widest mb-2">
                              OPEN
                            </span>
                            <p className="text-3xl font-black text-blue-600 leading-none mb-1">{src.open || 0}</p>
                            <p className="text-[10px] text-gray-500 font-medium">Needs attention</p>
                          </div>
                          <div onClick={() => nav('Fixed')} className="cursor-pointer bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-black uppercase tracking-widest mb-2">
                              FIXED
                            </span>
                            <p className="text-3xl font-black text-emerald-500 leading-none mb-1">{src.fixed || 0}</p>
                            <p className="text-[10px] text-gray-500 font-medium">Dev resolved</p>
                          </div>
                          <div onClick={() => nav('Closed')} className="cursor-pointer bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                            <span className="inline-block px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[9px] font-black uppercase tracking-widest mb-2">
                              CLOSED
                            </span>
                            <p className="text-3xl font-black text-green-700 leading-none mb-1">{src.closed || 0}</p>
                            <p className="text-[10px] text-gray-500 font-medium">Fully completed</p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status Breakdown</h3>
                            <div className="h-px bg-gray-100 flex-1" />
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {[
                              { label: 'Dev In Progress', value: src.inProgress,     border: 'border-l-orange-500', text: 'text-orange-700', icon: CodeBracketIcon,           iconColor: 'text-orange-400' },
                              { label: 'QA In Progress',  value: src.retesting,      border: 'border-l-indigo-500', text: 'text-indigo-700', icon: MagnifyingGlassIcon,       iconColor: 'text-indigo-400' },
                              { label: 'Fixed',           value: src.fixed,          border: 'border-l-emerald-500',text: 'text-emerald-700',icon: CheckCircleIcon,           iconColor: 'text-emerald-500' },
                              { label: 'On Hold',         value: src.onHold,         border: 'border-l-yellow-400', text: 'text-yellow-700', icon: PauseIcon,                 iconColor: 'text-yellow-500' },
                              { label: 'Recurring Issue', value: src.recurringIssue, border: 'border-l-red-500',    text: 'text-red-700',    icon: ArrowPathRoundedSquareIcon,iconColor: 'text-red-400' },
                              { label: 'Reopened',        value: src.reopened,       border: 'border-l-rose-500',   text: 'text-rose-700',   icon: ArrowUturnLeftIcon,        iconColor: 'text-rose-400' },
                              { label: 'Duplicate',       value: src.duplicate,      border: 'border-l-purple-500', text: 'text-purple-700', icon: DocumentDuplicateIcon,     iconColor: 'text-purple-400' },
                              { label: 'Rejected',        value: src.rejected,       border: 'border-l-gray-500',   text: 'text-gray-700',   icon: XCircleIcon,               iconColor: 'text-gray-400' },
                              { label: 'Deferred',        value: src.deferred,       border: sec.deferred.b,        text: sec.deferred.a,    icon: ClockIcon,                 iconColor: 'text-amber-400' },
                              { label: 'Not a Bug',       value: src.notABug,        border: 'border-l-slate-400',  text: 'text-slate-700',  icon: InformationCircleIcon,     iconColor: 'text-slate-400' },
                              { label: 'Deprecated',      value: src.deprecated,     border: 'border-l-gray-400',   text: 'text-gray-600',   icon: ArchiveBoxIcon,            iconColor: 'text-gray-400' },
                              { label: 'Archived',        value: src.archived,       border: 'border-l-slate-500',  text: 'text-slate-700',  icon: ArchiveBoxIcon,            iconColor: 'text-slate-500', isArchive: true },
                            ].map(st => (
                              <button key={st.label} onClick={() => nav(st.label, st.isArchive)} className="text-left bg-gray-50/50 border border-gray-100 border-l-[3px] rounded-lg p-3 hover:shadow-sm hover:bg-white transition-all group active:scale-[0.98]">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider truncate">{st.label}</p>
                                  {st.icon && <st.icon className={`h-3.5 w-3.5 ${st.iconColor} opacity-70 group-hover:opacity-100 shrink-0`} />}
                                </div>
                                <p className={`text-xl font-black ${st.text} leading-none`}>{st.value || 0}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* ── Existing Manager-Specific Section (Charts & Activity) ─────────────────────────── */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 grid grid-cols-1 gap-6">
                  {/* Trend Chart */}
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">30-Day Trend Analysis</h3>
                      <div className="flex gap-4 text-[10px] font-bold uppercase">
                        <span className="flex items-center text-blue-600"><span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>Open</span>
                        <span className="flex items-center text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></span>Fixed</span>
                        <span className="flex items-center text-amber-600"><span className="w-2 h-2 rounded-full bg-amber-500 mr-2"></span>Dev In Progress</span>
                      </div>
                    </div>
                    <div className="h-60 w-full font-mono">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.trendData.labels.map((date, i) => ({
                          name: date,
                          Open: data.trendData.open[i],
                          Fixed: data.trendData.fixed[i],
                          InProgress: data.trendData.inProgress[i]
                        }))} margin={{ top: 5, right: 20, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                          <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} minTickGap={30} />
                          <YAxis stroke="#94A3B8" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', color: '#111827', fontSize: '11px', fontWeight: 'bold', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }} 
                            itemStyle={{ padding: '2px 0' }} 
                          />
                          <Line type="monotone" dataKey="Open" stroke={COLORS.Open} strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                          <Line type="monotone" dataKey="Fixed" stroke={COLORS.Fixed} strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                          <Line type="monotone" dataKey="InProgress" stroke={COLORS.InProgress} strokeWidth={3} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Nested Charts */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">Status Distribution</h3>
                      <div className="flex-1 min-h-[160px] relative">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Open', value: data.statusDistribution.open },
                                { name: 'Fixed', value: data.statusDistribution.fixed },
                                { name: 'Dev In Progress', value: data.statusDistribution.inProgress }
                              ]}
                              cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={6} dataKey="value"
                            >
                              {[ COLORS.Open, COLORS.Fixed, COLORS.InProgress ].map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} stroke="none" />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', color: '#111827', fontSize: '11px', fontWeight: 'bold', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="text-center">
                            <span className="text-2xl font-black text-gray-900 leading-none">{data.kpis.total}</span>
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Total</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">Bug Density (Module)</h3>
                      <div className="h-40 w-full overflow-x-auto custom-scrollbar">
                        <div style={{ minWidth: Math.max(300, Object.keys(data.moduleDensity).length * 60) }}>
                          <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={Object.entries(data.moduleDensity).sort((a, b) => b[1] - a[1]).map(([key, val]) => ({ name: key, bugs: val }))}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                              <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 9, angle: -45, textAnchor: 'end', dy: 5 }} height={60} tickLine={false} axisLine={false} interval={0} />
                              <YAxis stroke="#94A3B8" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                              <Tooltip cursor={{ fill: '#F9FAFB' }} contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', color: '#111827', fontSize: '11px', fontWeight: 'bold', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }} />
                              <Bar dataKey="bugs" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={24} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar: Recent Activity */}
                <div className="bg-white border border-gray-100 rounded-xl shadow-sm flex flex-col h-[calc(100vh-140px)] sticky top-24">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest whitespace-nowrap">Recent Activity</h3>
                      {/* User Selection Dropdown */}
                      <select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        className="max-w-[150px] bg-white border border-gray-200 shadow-sm text-[10px] font-bold text-gray-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 rounded-lg py-1.5 px-2.5 cursor-pointer hover:border-gray-300 transition-all uppercase tracking-widest outline-none appearance-none"
                      >
                        <option value="all"> All Members </option>
                        {data.teamMembers && data.teamMembers.length > 0 && 
                          data.teamMembers.map(member => (
                            <option key={member._id} value={member._id}>
                              {member.name}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                    {data.recentActivities && data.recentActivities.length > 0 ? (
                      <ul className="space-y-4">
                        {data.recentActivities.map((activity, idx) => (
                          <li key={activity.id} className="relative pl-4">
                            {idx !== data.recentActivities.length - 1 && (
                              <div className="absolute top-2 left-[5px] w-px h-full bg-gray-200"></div>
                            )}
                            <div className={`absolute top-2 left-0 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm ${activity.action === 'created' ? 'bg-blue-500' : activity.action === 'fixed' ? 'bg-emerald-500' : activity.action === 'status' ? 'bg-amber-500' : 'bg-gray-400'}`}></div>
                            <div className="text-xs">
                              <div className="text-gray-500 mb-0.5">
                                <span className="font-semibold text-gray-800">{activity.userName}</span>
                                <span className="mx-1 opacity-60">•</span>
                                <span className="opacity-70">
                                  {(() => {
                                    const d = new Date(activity.timestamp);
                                    return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
                                  })()}
                                </span>
                              </div>
                              <p className="text-gray-600 leading-tight">
                                {getActionText(activity)} 
                                <button 
                                  onClick={() => navigate(`/redirect/bug/${activity.bugId}`)}
                                  className="ml-1 font-mono text-blue-600 bg-blue-50 px-1 rounded border border-blue-100 hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-pointer"
                                >
                                  {activity.bugId}
                                </button>
                              </p>
                              {activity.details && (
                                <p className="mt-1 text-gray-400 italic max-w-xs truncate">{activity.details}</p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center p-4">
                        <ClockIcon className="h-8 w-8 text-gray-200 mb-2" />
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-relaxed"> No recent activity <br/> found </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
